import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { strings } from '@constants'
import { UpdateFinancialsBusinessDetail, getCategories, getUserByID } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { showDateFormat } from '@utils'
import { DrawerBodyWrapper, DrawerFooterWrapper, TOAST_TYPE } from '@base'
import { Button, MultiSelect } from '@baseV2'

import { InputsWrapper, Field, FieldLabel, FieldValue, SectionActions } from '../styles'

const TAG_FIELDS = [
	{ key: 'business_types', label: strings(['type', 'of', 'business']), placeholder: strings(['select', 'type', 'of', 'business']) },
	{ key: 'projects_key', label: strings(['key', 'projects']), placeholder: strings(['select', 'key', 'projects']) },
	{ key: 'projects_completed', label: strings(['completed', 'projects']), placeholder: strings(['select', 'completed', 'projects']) },
	{ key: 'projects_mbook', label: strings(['mbook', 'projects']), placeholder: strings(['select', 'mbook', 'projects']) },
	{ key: 'departments', label: strings(['department', 'list']), placeholder: strings(['select', 'department', 'list']) },
]

const buildTags = (arr) => (arr?.length ? arr.map((t) => ({ title: t })) : [])

const deriveState = (data, categories) => ({
	business_types: buildTags(data?.business_detail?.business_types),
	projects_key: buildTags(data?.business_detail?.projects_key),
	projects_completed: buildTags(data?.business_detail?.projects_completed),
	projects_mbook: buildTags(data?.business_detail?.projects_mbook),
	departments: buildTags(data?.business_detail?.departments),
	materials: categories?.filter((c) => data?.business_detail?.materials?.includes(c?.category_name)) ?? [],
})

const BusinessDetails = ({ data, userDetails, organisationId, update }) => {
	const dispatch = useDispatch()
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const [categories, setCategories] = useState(null)
	const [createdByName, setCreatedByName] = useState('')
	const [formState, setFormState] = useState(() => deriveState(data, null))

	useEffect(() => {
		getCategories(0)
			.then((res) => setCategories(res?.data ?? []))
			.catch((err) => console.log(err))
		if (data?.business_detail?.created_by) {
			getUserByID(data?.business_detail?.created_by)
				.then((res) => setCreatedByName(res?.full_name ?? ''))
				.catch((err) => console.log(err))
		}
	}, [data])

	useEffect(() => {
		setFormState(deriveState(data, categories))
	}, [data, categories])

	const updateField = (key) => (value) => setFormState((_s) => ({ ..._s, [key]: value }))

	const submit = () => {
		dispatch(showLoader(true))
		const titles = (arr) => (arr?.length ? arr.map((i) => i?.title) : null)
		UpdateFinancialsBusinessDetail(
			titles(formState.business_types),
			titles(formState.projects_key),
			titles(formState.projects_completed),
			titles(formState.projects_mbook),
			formState.materials?.length ? formState.materials.map((i) => i?.category_name) : null,
			titles(formState.departments),
			userDetails,
			organisationId,
			loggedInUser.getUserDetails()?.user_id
		)
			.then(() => {
				dispatch(showToast(true, strings(['business', 'details', 'updated', 'successfully']), { type: TOAST_TYPE.SUCCESS }))
				dispatch(toggleDrawer(false))
				update?.()
			})
			.catch(() => dispatch(showToast(true, strings('error_something_went_wrong'), { type: TOAST_TYPE.ERROR })))
			.finally(() => dispatch(showLoader(false)))
	}

	const createdAt = useMemo(() => (data?.business_detail?.created_at ? showDateFormat(new Date(data?.business_detail?.created_at)) : ''), [data])

	return (
		<>
			<DrawerBodyWrapper>
				<InputsWrapper>
					{TAG_FIELDS.map((field) => (
						<MultiSelect
							key={field.key}
							id={`bd-${field.key}-v2`}
							label={field.label}
							placeholder={field.placeholder}
							data={[]}
							primaryKey='title'
							displayKey='title'
							value={formState[field.key] || []}
							onChange={updateField(field.key)}
							createNewOption
						/>
					))}
					<MultiSelect
						id='bd-materials-v2'
						label={strings('materials')}
						placeholder={!categories ? 'Loading...' : strings(['select', 'materials'])}
						data={categories ?? []}
						primaryKey='category_id'
						displayKey='category_name'
						value={formState.materials || []}
						onChange={updateField('materials')}
						disabled={!categories}
					/>

					{(createdByName || createdAt) && (
						<SectionActions>
							{createdByName && (
								<Field>
									<FieldLabel>{strings('created', 'by')}</FieldLabel>
									<FieldValue>{createdByName}</FieldValue>
								</Field>
							)}
							{createdAt && (
								<Field>
									<FieldLabel>{strings('created', 'at')}</FieldLabel>
									<FieldValue>{createdAt}</FieldValue>
								</Field>
							)}
						</SectionActions>
					)}
				</InputsWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button color='primary' onClick={submit}>
					{data ? strings('edit') : strings('add')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default BusinessDetails
